import React from 'react';

import { CartesianGrid, Line, LineChart, ResponsiveContainer, XAxis, YAxis } from 'recharts';

import ReportChartTooltip from '~/components/Common/ReportChartTooltip';

import { type Reading, numeric, rowsFor } from '../ContributionMargin/liveModel';

import { config } from './config';
import type { MarketingPresentation } from './liveDataAdapter';
import { type Filters, type Row, format, value } from './presentationModel';
import { salesGmarShares } from './workflows';

const popMetrics = ['ROAS (Margin) (BR)', 'GM (BR)', 'Costs', 'CPC', 'Orders', 'CR (Orders/Sessions)'];
export function salesReadings(rows: Array<Row>) {
  return salesGmarShares(rowsFor({ cardId: 'sales', status: 'ready', rows }, config.ratios, false, config.derived));
}
export function salesColumns(rows: Array<Reading>) {
  return [...new Set(rows.flatMap((row) => Object.keys(row)))].filter(
    (key) => !['split_one', 'split_two'].includes(key),
  );
}
export default function SupplementalViews({ model, filters }: { model: MarketingPresentation; filters: Filters }) {
  if (filters.view === 'PoP') {
    const bucket = (rows: Array<Row>, metric: string) => {
      const dates = [...new Set(rows.map((row) => String(row.bucket)))].sort();
      return dates.map((date) =>
        value(
          rows.filter((row) => row.bucket === date),
          metric,
        ),
      );
    };
    return (
      <div className="pm-paired-plots">
        {popMetrics.map((metric) => {
          const current = bucket(model.daily, metric),
            previous = bucket(model.previous, metric);
          const data = Array.from({ length: Math.max(current.length, previous.length) }, (_, index) => ({
            day: index + 1,
            current: current[index] ?? null,
            previous: previous[index] ?? null,
          }));
          return (
            <section className="pm-plot" key={metric}>
              <h2>{metric} · CP / PP</h2>
              <div className="pm-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data}>
                    <CartesianGrid vertical={false} stroke="#E3E7E2" />
                    <XAxis dataKey="day" />
                    <YAxis width={72} tickFormatter={(v) => format(v, metric, true)} />
                    <ReportChartTooltip formatter={(v) => format(typeof v === 'number' ? v : null, metric)} />
                    <Line
                      dataKey="current"
                      name="Current period"
                      stroke="#0F464F"
                      dot={false}
                      isAnimationActive={false}
                      connectNulls={false}
                    />
                    <Line
                      dataKey="previous"
                      name="Previous period"
                      stroke="#76BFAA"
                      strokeDasharray="5 3"
                      dot={false}
                      isAnimationActive={false}
                      connectNulls={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <p className="pm-source">
                Period buckets align by position. Ratios use additive operands within each bucket; missing buckets
                remain Unknown. Ordinary discount metrics retain their separately recovered BR/AR definitions.
              </p>
            </section>
          );
        })}
      </div>
    );
  }
  if (filters.view === 'Monthly') {
    const years = [...new Set(model.monthly.map((row) => String(row.p_date).slice(0, 4)))].sort();
    const months = Array.from({ length: 12 }, (_, index) => String(index + 1).padStart(2, '0'));
    const at = (year: string, month: string) => {
      const row = model.monthly.find((row) => String(row.p_date).slice(0, 7) === year + '-' + month);
      return numeric(
        rowsFor(
          { cardId: 'monthly', status: 'ready', rows: row ? [row] : [] },
          config.ratios,
          false,
          config.derived,
        )[0]?.[model.chartMetric],
      );
    };
    const data = months.map((month) => ({
      month,
      ...Object.fromEntries(years.map((year) => [year, at(year, month)])),
    }));
    return (
      <section className="pm-tabular">
        <h2>Performance YoY · {model.chartMetric}</h2>
        <div className="pm-chart">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data}>
              <CartesianGrid vertical={false} stroke="#E3E7E2" />
              <XAxis dataKey="month" />
              <YAxis width={72} />
              <ReportChartTooltip />
              {years.map((year, index) => (
                <Line
                  key={year}
                  dataKey={year}
                  stroke={index % 2 ? '#76BFAA' : '#0F464F'}
                  dot={false}
                  connectNulls={false}
                  isAnimationActive={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="pm-scroll">
          <table>
            <thead>
              <tr>
                <th>Year · measure</th>
                {months.map((month) => (
                  <th key={month}>{month}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {years.flatMap((year) => [
                <tr key={year}>
                  <th>
                    {year} · {filters.metric}
                  </th>
                  {months.map((month) => (
                    <td key={month}>{format(at(year, month), filters.metric)}</td>
                  ))}
                </tr>,
                <tr key={year + '-growth'}>
                  <th>{year} · YoY growth</th>
                  {months.map((month) => {
                    const current = at(year, month),
                      prior = at(String(Number(year) - 1), month);
                    return (
                      <td key={month}>
                        {format(
                          current === null || prior === null || prior === 0 ? null : current / prior - 1,
                          'percent',
                        )}
                      </td>
                    );
                  })}
                </tr>,
              ])}
            </tbody>
          </table>
        </div>
        <p className="pm-source">
          Fixed Last Click 30D Non-Passive attribution. Calendar months within the selected source window; current edge
          months may be partial. Missing months remain Unknown.
        </p>
      </section>
    );
  }
  const rows = salesReadings(filters.timeSplit === 'Date Cadence' ? model.salesDaily : model.sales),
    total = salesReadings(model.salesTotal),
    columns = salesColumns([...rows, ...total]);
  const daily = rowsFor(
    { cardId: 'sales_daily', status: 'ready', rows: model.salesDaily },
    config.ratios,
    false,
    config.derived,
  );
  const splitKeys = [...new Set(daily.map((row) => JSON.stringify([row.split_one, row.split_two])))];
  const dates = [...new Set(daily.map((row) => String(row.bucket)))].sort();
  const salesCharts = [model.chartMetric, model.secondMetric].map((metric, chartIndex) => {
    const relative = chartIndex === 0 ? model.firstRelative : model.secondRelative;
    const points = dates.map((date) => {
      const values = splitKeys.map((key) =>
        numeric(
          daily.find((row) => row.bucket === date && JSON.stringify([row.split_one, row.split_two]) === key)?.[metric],
        ),
      );
      const denominator = values.reduce<number>((sum, amount) => sum + (amount ?? 0), 0);
      return {
        date,
        ...Object.fromEntries(
          values.map((amount, index) => [
            'series' + index,
            relative ? (amount === null || !denominator ? null : amount / denominator) : amount,
          ]),
        ),
      };
    });
    return (
      <section className="pm-plot" key={chartIndex}>
        <h2>
          {metric} · {relative ? 'relative' : 'absolute'}
        </h2>
        <div className="pm-chart">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={points}>
              <CartesianGrid vertical={false} stroke="#E3E7E2" />
              <XAxis dataKey="date" />
              <YAxis width={72} />
              <ReportChartTooltip />
              {splitKeys.slice(0, 24).map((key, index) => (
                <Line
                  key={key}
                  name={key}
                  dataKey={'series' + index}
                  stroke={index % 2 ? '#76BFAA' : '#0F464F'}
                  dot={false}
                  connectNulls={false}
                  isAnimationActive={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
        <p className="pm-source">
          Each source split tuple is separate. Daily source COUNTD values are never summed into period totals. Up to 24
          groups plotted; every source group remains in the table and CSV.
        </p>
      </section>
    );
  });
  return (
    <section className="pm-tabular">
      <h2>Sales · source attribution groups</h2>
      <div className="pm-paired-plots">{salesCharts}</div>
      <p className="pm-source">
        analytics.sales_attribution · optional transaction bounds are Sydney civil timestamps; order bounds are UTC
        dates. Orders and customers below use their independent source COUNTD. Grouped distinct counts are never summed
        into the grand total. GM AR share uses the complete selected split population, separately for each period; the
        original Sales All PctTotal partition remains unverified.
      </p>
      <div className="pm-scroll" tabIndex={0} aria-label="Sales full table">
        <table>
          <thead>
            <tr>
              <th>Data Split 1</th>
              <th>Data Split 2</th>
              {columns.map((column) => (
                <th key={column}>{column}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[...total.map((row) => ({ ...row, split_one: 'Grand Total', split_two: '' })), ...rows].map(
              (row, index) => (
                <tr key={index} className={index === 0 && total.length ? 'pm-total' : ''}>
                  <th>{String(row.split_one ?? 'Unknown')}</th>
                  <th>{String(row.split_two ?? 'Unknown')}</th>
                  {columns.map((column) => (
                    <td key={column}>
                      {typeof row[column] === 'string'
                        ? String(row[column])
                        : format(
                            numeric(row[column]),
                            column.includes('share') || column.includes('rate') ? 'percent' : 'number',
                          )}
                    </td>
                  ))}
                </tr>
              ),
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
